import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js'; // NEW CHANGE 3
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection } from '@adornis/baseql/server/collections.js'; // NEW CHANGE 3 (A1)
import { deriveKnowledgeQueueAsync } from './knowledge-queue.js';

// NEW CHANGE 3 — CRM lifecycle status for the reconcile cron
export const CRM_STATUSES = ['not-attempted', 'pending-lookup', 'confirmed', 'failed', 'manual-review'] as const;
export type CrmStatus = (typeof CRM_STATUSES)[number];

@Entity()
export class LocalTicket extends MongoEntity {
  static override _class = 'LocalTicket';
  static override _collectionName = 'local_tickets';

  constructor(data: PartialEntityData<LocalTicket>) {
    super(data);
  }

  // System / identity
  @Field(() => String) ticketNumber: Maybe<string>;
  // CRM ticket number (CAS-xxx) — populated when CRM createCase succeeds.
  // Falls back to ticketNumber (LOC-xxx) if CRM failed/unavailable.
  @Field(() => String) crmTicketNumber: Maybe<string>;
  @Field(() => Date) createdOn: Maybe<Date>;
  @Field(() => Date) modifiedOn: Maybe<Date>;
  @Field(() => Date) escalatedOn: Maybe<Date>;

  // Durable creation command. Legacy rows have no commandState and are never replayed.
  @Field(() => String) commandState: Maybe<'prepared' | 'sending' | 'confirmed' | 'unconfirmed' | 'not-attempted'>;
  @Field(() => String) crmCaseId: Maybe<string>;
  @Field(() => String) manualReviewReason: Maybe<string>;
  @Field(() => String) desiredQueueName: Maybe<string>;
  @Field(() => String) queueStatus: Maybe<'pending' | 'retrying' | 'assigned' | 'manual-review' | 'not-attempted'>;
  @Field(() => Date) queueRetryAt: Maybe<Date>;
  @Field(() => Int) queueAttempts: Maybe<number>;
  @Field(() => String) escalationStatus: Maybe<'requested' | 'confirmed' | 'unconfirmed'>;

  // Caller
  @Field(() => String) phoneNumber: Maybe<string>;
  @Field(() => String) callerName: Maybe<string>;
  @Field(() => String) callerEmail: Maybe<string>;

  // Account (flat — no joins)
  @Field(() => String) accountName: Maybe<string>;
  @Field(() => String) accountNumber: Maybe<string>;

  // Machine (flat — dossierNumber is the lookup key into machines)
  @Field(() => String) dossierNumber: Maybe<string>;
  @Field(() => String) machineName: Maybe<string>;

  // Ticket body
  @Field(() => String) description: Maybe<string>;
  @Field(() => String) status: Maybe<'Open' | 'Closed' | 'ClosureRequested' | 'Archived'>;
  @Field(() => String) priority: Maybe<'Low' | 'Normal' | 'High'>;
  @Field(() => String) resolution: Maybe<string>;

  // Flag — Path C 2.2 captures (accountNumber null)
  @Field(() => Boolean) incompleteCapture: Maybe<boolean>;

  // Append-only notes (from add_info_to_ticket)
  @Field(() => [String]) notes: Maybe<string[]>;

  // Hidden / debug
  @Field(() => String) sessionID: Maybe<string>;
  @Field(() => [String]) callLog: Maybe<string[]>;

  // NEW CHANGE 3 — CRM reconcile bookkeeping
  // Lifecycle of CRM case lookup/creation. Undefined on legacy rows → treat as 'not-attempted'.
  @Field(() => String) crmStatus: Maybe<CrmStatus>;
  // NEW CHANGE 3 — Per-call session UUID (bookkeeping; correlates with call provider session)
  @Field(() => String) callUuid: Maybe<string>;
  // NEW CHANGE 3 — Case IDs existing BEFORE createCase ran (for diff matching when CRM ACK is missing)
  @Field(() => [String]) snapshotBeforeIds: Maybe<string[]>;
  // NEW CHANGE 3 — When the reconcile cron should next attempt a CRM lookup (null = no scheduled retry)
  @Field(() => Date) scheduledLookupAt: Maybe<Date>;
  // NEW CHANGE 3 — Backoff index (0..5); incremented on each failed lookup attempt
  @Field(() => Int) retryCount: Maybe<number>;
  // Legacy application-email timestamp, retained for historical rows; not CRM delivery status.
  // N09: no new application email is sent or scheduled, regardless of this value.
  @Field(() => Date) emailSentAt: Maybe<Date>;

  // 🆕 2026-05-26 — Optional FK to CallSession (cost analytics).
  // CallSession is the source of truth for per-call LLM cost data;
  // populated for ALL calls (ticket OR no-ticket), so cost analytics
  // doesn't lose abandoned/redirected/incomplete calls.
  // Joined by callUuid (string) in the cost-analytics tab.
  // (No fields needed here — query CallSession by callUuid directly.)

  // Knowledge queue: delegates to shared helper (single source of truth)
  @Field(() => String, {
    resolve: function (this: LocalTicket) {
      return async () => deriveKnowledgeQueueAsync(this.dossierNumber);
    },
  })
  knowledgeQueue: Maybe<string>;

  static override async transformBeforeUpdate(
    oldEntity: PartialEntityData<LocalTicket>,
    update: LocalTicket,
  ): Promise<void> {
    update.modifiedOn = new Date();
    return super.transformBeforeUpdate(oldEntity, update);
  }
}

// NEW CHANGE 3 (A1) — Index registration for the reconcile cron query:
//   collection.find({ crmStatus: 'pending-lookup', scheduledLookupAt: { $lte: now } })
// Mongo's createIndex is idempotent (no-op when an identical index already exists),
// so this is safe to call on every server boot.
export async function ensureLocalTicketIndexes(): Promise<void> {
  const collection = await getCollection<LocalTicket>(LocalTicket._class);
  await collection.createIndex({ crmStatus: 1, scheduledLookupAt: 1 }, { name: 'crmStatus_1_scheduledLookupAt_1' });
}
